import React, { useState, useCallback, type SyntheticEvent } from 'react';
import Button from '@atlaskit/button';
import ModalDialog, {
	ModalHeader,
	ModalBody,
	ModalFooter,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { RadioGroup } from '@atlaskit/radio';
import type { OptionsPropType } from '@atlaskit/radio/types';
import { ViewTracker } from '@atlassian/jira-analytics-web-react/src';
import { ShortcutScope } from '@atlassian/jira-common-components-keyboard-shortcuts';
import { useIntl } from '@atlassian/jira-intl';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	MODAL,
	ContextualAnalyticsData,
} from '@atlassian/jira-product-analytics-bridge';
import { MoveCardsSelectTransitionType } from '../../../model/modals/types';
import type { Transition } from '../../../model/transition/types';
import { useBacklogDispatch, useBacklogSelector } from '../../../state';
import { cardsMoveCancel, cardsMoveNonOptimisticRequest } from '../../../state/actions/cards/move';
import { closeModal } from '../../../state/actions/modals';
import { getOpenModalData } from '../../../state/selectors/modals';
import { messages } from './messages';

const getOptions = (transitions: Transition[]): OptionsPropType =>
	transitions.map((transition) => ({
		name: 'transition',
		value: String(transition.id),
		label: transition.name,
	}));

export function SelectTransitionsModal() {
	const [selectedTransitionId, setSelectedTransitionId] = useState<number | undefined>(undefined);
	const [isDisabled, setIsDisabled] = useState<boolean>(true);

	const { formatMessage } = useIntl();

	const dispatch = useBacklogDispatch();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const modalAnalyticsEvent = createAnalyticsEvent({
		actionSubject: 'button',
		action: 'clicked',
	});

	const modalData = useBacklogSelector((state) => {
		const modal = getOpenModalData(state);
		return modal && modal.type === MoveCardsSelectTransitionType ? modal.payload : null;
	});

	const onSelectionChange = useCallback(
		({ currentTarget: { value } }: SyntheticEvent<HTMLInputElement>) => {
			if (value) {
				setSelectedTransitionId(Number(value));
				setIsDisabled(false);
			}
		},
		[setIsDisabled, setSelectedTransitionId],
	);

	if (!modalData) {
		return null;
	}

	const { cardKeys, transitions, moveCardsActionPayload, optimisticId, analyticsEvent } = modalData;

	const onCancel = () => {
		fireUIAnalytics(modalAnalyticsEvent, 'cancelButton');
		dispatch(cardsMoveCancel(optimisticId, analyticsEvent));
		dispatch(closeModal());
	};

	const onChoose = () => {
		fireUIAnalytics(modalAnalyticsEvent, 'chooseTransitionButton');
		selectedTransitionId &&
			dispatch(
				cardsMoveNonOptimisticRequest(
					optimisticId,
					{
						...moveCardsActionPayload,
						selectedTransition: transitions.find(
							(transition) => transition.id === selectedTransitionId,
						),
					},
					analyticsEvent,
				),
			);
		dispatch(closeModal());
	};

	return (
		<ContextualAnalyticsData sourceType={MODAL} sourceName="selectTransition">
			<ModalTransition>
				<ShortcutScope>
					<ModalDialog onClose={onCancel}>
						<ModalHeader>
							<ModalTitle>{formatMessage(messages.title)}</ModalTitle>
						</ModalHeader>
						<ModalBody>
							<p>
								{formatMessage(messages.description, {
									count: transitions.length,
									cardKeys: cardKeys.join(', '),
								})}
							</p>
							<RadioGroup onChange={onSelectionChange} options={getOptions(transitions)} />
							<ViewTracker />
						</ModalBody>
						<ModalFooter>
							<Button appearance="subtle" onClick={onCancel}>
								{formatMessage(messages.cancel)}
							</Button>
							<Button isDisabled={isDisabled} appearance="primary" onClick={onChoose} autoFocus>
								{formatMessage(messages.choose)}
							</Button>
						</ModalFooter>
					</ModalDialog>
				</ShortcutScope>
			</ModalTransition>
		</ContextualAnalyticsData>
	);
}
